import { sectionRegistry } from './sectionRegistry';
import React, { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { type Block, type SectionInstance } from '@aquilixshop/ui-react';
import { bt, translateFields } from '../i18n';
import { blockDisplayName } from '../shell/sectionVariantLabel';
import { measureBox } from './measureBox';
import { scrollableAncestors } from './revealInTracks';

/** La boîte peinte d'un nœud du canvas — vit dans `measureBox.ts`, re-exportée ici par commodité. */
export { measureBox };

/**
 * The measured canvas FRAME — the shared primitive behind <SelectionFrame> (what IS selected) and
 * <HoverFrame> (what a click WOULD select).
 *
 * Why an overlay and not CSS `outline`: every block is wrapped by `BlockRenderer` in a
 * `<span style="display:contents">` carrying `data-aqx-block-id`. A `display:contents` element generates
 * NO box, so `outline`/`border`/`background` on it paint NOTHING — the block "select" set the id but no
 * frame ever appeared (heading/text/button all rendered through this path). We instead MEASURE the target's
 * real painted box (the union of the wrapper's element children) and paint an absolutely-positioned frame
 * over it. This works uniformly for display:contents blocks, real-element blocks, field hotspots (a heading
 * `<h2>` bound to a setting) AND whole sections, and lets us hang a "Heading"/"Button"/… label tab on it.
 *
 * The overlay lives INSIDE the `[data-aqx-page]` scroll content and is `position:absolute` within it, so it
 * scrolls in lockstep with the sections — the offset it stores (target box − page box) is scroll-invariant,
 * so we only recompute on target change, content resize (ResizeObserver), and window resize.
 */
export type Box = { top: number; left: number; width: number; height: number };

/** Find a block anywhere in a section's nested tree (Shrine recursive block model). */
export function findBlockInTree(blocks: Block[], id: string): Block | undefined {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.blocks) {
      const found = findBlockInTree(b.blocks, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function blockLabel(section: SectionInstance | undefined, blockId: string): string {
  if (!section) return bt('chrome.canvas.block', 'Block');
  const block = findBlockInTree(section.blocks ?? [], blockId);
  if (!block) return bt('chrome.canvas.block', 'Block');
  const bschema = sectionRegistry[section.type]?.schema.blocks?.find((b) => b.type === block.type);
  // The schema's `name` is English display text; the seller reads it in her own language (0051 lot 4).
  return bschema ? blockDisplayName(section.type, block.type, bschema.name) : block.type;
}

export function fieldLabel(section: SectionInstance | undefined, fieldId: string): string {
  const type = section?.type ?? '';
  const field = sectionRegistry[type]?.schema.fields.find((f) => f.id === fieldId);
  // Same seam as the inspector: the label travels through the dictionary, the id never does.
  return field ? (translateFields(type, [field])[0]?.label ?? field.label) : fieldId;
}

/**
 * Paints a labelled frame over `target`, positioned relative to the `[data-aqx-page]` root.
 *
 * `variant`: `selected` = solid (this IS the current selection) · `hover` = dashed (this is only what a
 * click would select — nothing is selected yet, so no panel opens).
 */
/** Ce qu'on garde entre la bulle et le bord du canvas quand il faut la ramener. */
const EDGE_MARGIN = 12;

/**
 * La hauteur de l'étiquette, et celle du bouton d'action à côté d'elle (`.bld-framebtn`).
 *
 * Le bouton est PLUS HAUT depuis le 27/08 (« it's soo small, even me I don't see it ») : on le
 * recentre donc sur la ligne de l'étiquette au lieu de l'aligner par le haut, sinon il pendrait de
 * huit pixels sous elle.
 */
const LABEL_HEIGHT = 20;
const ACTION_HEIGHT = 30;

export function CanvasFrame({
  pageRef,
  target,
  label,
  variant = 'selected',
  // Builder chrome layer — see the `--bld-z-*` ladder in shell.css. It must clear anything a storefront
  // section paints of its own (header sticky 50 / 60, countdown popup 55, sales-pop 60, header search
  // panel 65, open drawer 75), or the frame is drawn UNDER the very thing it is supposed to outline.
  zIndex = 203,
  inset = false,
  labelSide = 'left',
  ring = true,
  action,
  overlay,
}: {
  pageRef: RefObject<HTMLDivElement | null>;
  target: Element | null;
  label: string;
  variant?: 'selected' | 'hover';
  zIndex?: number;
  /** Draw the outline INSIDE the box — a full-bleed section frame would otherwise be clipped by the canvas. */
  inset?: boolean;
  /** Which corner the label tab hangs from — `right` keeps a section label clear of the drag grip. */
  labelSide?: 'left' | 'right';
  /**
   * Faux → PAS de trait autour de la boîte, seulement l'étiquette et ses affordances. C'est le cas
   * d'une SECTION sélectionnée : `EditableSection` peint déjà son propre anneau, et un second trait
   * par-dessus donnerait deux cadres pour une seule chose.
   */
  ring?: boolean;
  /**
   * Un bouton COLLÉ À L'ÉTIQUETTE (27/08). La barre d'outils « Demander un changement » est ancrée
   * au haut de la SECTION : sur une fiche produit de trois écrans de haut, sélectionner un bloc du
   * bas la laissait hors de vue — « je vois pas ask for changes, elle doit toujours être près du
   * composant sélectionné ». Le cadre, lui, est TOUJOURS sur l'élément visé.
   */
  action?: React.ReactNode;
  /** Ce qui s'ouvre au clic sur [action] — ancré sous l'étiquette, donc sous les yeux. */
  overlay?: React.ReactNode;
}) {
  const [box, setBox] = useState<Box | null>(null);
  /**
   * DE COMBIEN LA BULLE DOIT RECULER POUR RESTER DANS LE CANVAS (27/08, vu en capture).
   *
   * Elle s'ouvre alignée sur le bord gauche de ce qui est sélectionné. Un bloc situé dans la colonne
   * de droite d'une fiche produit la poussait donc SOUS le panneau des réglages : la phrase était
   * coupée en plein mot, les menus et le bouton « Régénérer » hors d'atteinte. On la mesure et on la
   * ramène — jamais au-delà du bord gauche, où elle sortirait de l'autre côté.
   */
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlayShift, setOverlayShift] = useState(0);
  /**
   * LA LARGEUR RÉELLE DE L'ÉTIQUETTE — mesurée, plus estimée (27/08). Un « ~6,2 px par caractère »
   * tombait juste sur « Slideshow » et faux sur « Comparison table » : le bouton chevauchait la fin
   * du nom. Une étiquette se mesure, elle ne se devine pas.
   */
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page || !target) {
      setBox(null);
      return;
    }

    const reposition = () => {
      const pr = page.getBoundingClientRect();
      const m = measureBox(target);
      if (!m) {
        setBox(null);
        return;
      }
      const next = { top: m.t - pr.top, left: m.l - pr.left, width: m.r - m.l, height: m.b - m.t };
      // Même boîte ⇒ pas de re-rendu : une piste qui défile émet un `scroll` par image, et la plupart
      // d'entre eux ne déplacent rien de ce qu'on peint.
      setBox((prev) =>
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };
    reposition();

    // Keep the frame glued to the element as it (or the canvas) reflows. `ResizeObserver` is guarded so
    // the component is inert under jsdom (tests) / any non-browser host — it just won't auto-reposition.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reposition) : null;
    if (ro) {
      ro.observe(page);
      if (getComputedStyle(target).display === 'contents') {
        for (const c of Array.from(target.children)) ro.observe(c);
      } else {
        ro.observe(target);
      }
    }
    window.addEventListener('resize', reposition);
    /*
      LES PISTES QUI DÉFILENT SOUS LE CADRE (fondatrice, 19/09/2026).

      L'écart mémorisé (boîte de la cible − boîte de la page) est invariant au défilement de l'APERÇU,
      qui emporte la page et la cible ensemble. Il ne l'est PAS quand un conteneur intérieur défile :
      un carrousel de Multicolonnes déplace ses cartes, la page ne bouge pas, et le cadre reste peint
      sur la carte d'avant — on croit alors avoir sélectionné une colonne et on en voit une autre.
      `ResizeObserver` ne voit rien de tout ça : rien n'a changé de taille.
    */
    const scrollers = scrollableAncestors(target, page);
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        reposition();
      });
    };
    for (const sc of scrollers) sc.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', reposition);
      for (const sc of scrollers) sc.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, pageRef]);

  useLayoutEffect(() => {
    setLabelWidth(labelRef.current?.getBoundingClientRect().width ?? 0);
  }, [label, box, action]);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const el = overlayRef.current;
    if (!page || !el || !box) {
      setOverlayShift(0);
      return;
    }
    const width = el.getBoundingClientRect().width;
    // ELLE RESTE ENTRE LES DEUX BORDS (14/09). L'ancien calcul ne savait que RECULER : si la bulle
    // était plus large que le canvas — ce que le cadre mobile de 390 px provoquait — `maxLeft`
    // devenait négatif et le recul la sortait par la GAUCHE, où `overflow: clip` la décapite (« e
    // retouche… », « avec » amputé). On vise maintenant une position bornée des deux côtés, donc une
    // section pleine largeur ne colle plus non plus au bord.
    const maxLeft = page.clientWidth - width - EDGE_MARGIN;
    const left = Math.min(Math.max(box.left, EDGE_MARGIN), Math.max(EDGE_MARGIN, maxLeft));
    setOverlayShift(left - box.left);
  }, [box, overlay, pageRef]);

  if (!box) return null;

  const hover = variant === 'hover';
  // Avant la première mesure (un rendu), une estimation évite que le bouton naisse SUR l'étiquette.
  const labelOffset = labelWidth || Math.round(label.length * 6.2) + 18;
  // Near the top of the canvas, tuck the label INSIDE the frame so it isn't clipped by the page edge.
  const labelAbove = box.top >= 22;

  const frame = (
    <div
      aria-hidden="true"
      data-bld-frame={variant}
      style={{
        position: 'absolute',
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        pointerEvents: 'none',
        zIndex,
        ...(ring
          ? {
              outline: `2px ${hover ? 'dashed' : 'solid'} var(--aqx-purple, #6d4aff)`,
              outlineOffset: inset ? -2 : 2,
            }
          : {}),
        borderRadius: 3,
      }}
    >
      <span
        ref={labelRef}
        style={{
          position: 'absolute',
          top: labelAbove ? -22 : 2,
          ...(labelSide === 'right' ? { right: -2 } : { left: -2 }),
          background: 'var(--aqx-purple, #6d4aff)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          lineHeight: '16px',
          padding: '2px 8px',
          borderRadius: labelAbove ? '4px 4px 4px 0' : 4,
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          opacity: hover ? 0.9 : 1,
          borderTopRightRadius: labelSide === 'right' && labelAbove ? 4 : undefined,
          borderBottomRightRadius: labelSide === 'right' && labelAbove ? 0 : undefined,
        }}
      >
        {label}
      </span>
    </div>
  );

  // LE BOUTON NE PEUT PAS VIVRE DANS LE CADRE (27/08). Le cadre est `aria-hidden` — c'est une
  // décoration, elle n'a rien à dire à un lecteur d'écran. Un bouton posé dedans disparaît du même
  // coup de l'arbre d'accessibilité, tout en restant atteignable au clavier : la faute exacte que la
  // recette axe interdit. Il vit donc dans un CALQUE JUMEAU, aux mêmes coordonnées, bien visible.
  const affordances =
    action || overlay ? (
      <div
        data-bld-frame-actions={variant}
        style={{
          position: 'absolute',
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          pointerEvents: 'none',
          // Voir l'échelle `--bld-z-*` dans shell.css : ce calque doit passer AU-DESSUS de la bande
          // d'insertion, qui tend une zone d'accroche invisible juste là où l'étiquette se pose.
          zIndex: 'var(--bld-z-selaction, 206)',
        }}
      >
        {action && (
          <div
            style={{
              position: 'absolute',
              top: (labelAbove ? -22 : 2) - (ACTION_HEIGHT - LABEL_HEIGHT) / 2,
              ...(labelSide === 'right' ? { right: -2 } : { left: -2 }),
              transform: `translateX(${labelSide === 'right' ? '-' : ''}${labelOffset + 6}px)`,
              pointerEvents: 'auto',
            }}
          >
            {action}
          </div>
        )}
        {overlay && (
          <div
            ref={overlayRef}
            style={{
              position: 'absolute',
              top: labelAbove ? 4 : 26,
              ...(labelSide === 'right' ? { right: 0 } : { left: 0 }),
              transform: overlayShift ? `translateX(${overlayShift}px)` : undefined,
              pointerEvents: 'auto',
            }}
          >
            {overlay}
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      {frame}
      {affordances}
    </>
  );
}
